import "./styles.scss";

import { FlagIcon } from "react-flag-kit";
import { ClipLoader } from "react-spinners";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  getPlan,
  getAdminUserInfo,
  getAffiliateInfo,
  getUserInformation,
  subscriptionsStripeGetPlans,
} from "../../store/middleware";
import logoPNG from "../../assets/images/lingo_small.png";
import exStudentEN from "../../assets/video/exStudentEN.mp4";
import exStudentPT from "../../assets/video/exStudentPT.mp4";
import exStudentES from "../../assets/video/exStudentES.mp4";
import exStudentFR from "../../assets/video/exStudentFR.mp4";
import { decryptIfEncrypted } from "../../utils/fieldDecryptor";
import effectiveCharacterPlans from "../../assets/images/personagem_planos_effective.png";
import essentialCharacterPlans from "../../assets/images/personagem_planos_essential.png";
import evolutionCharacterPlans from "../../assets/images/personagem_planos_evolution.png";

const formatTime = (time) => {
  if (!time || Number.isNaN(time)) return "0:00";
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60)
    .toString()
    .padStart(2, "0");
  return `${minutes}:${seconds}`;
};

const languageMap = {
  en: {
    video: exStudentEN,
    name: "nameEnglish",
    description: "descriptionEnglish",
  },
  pt: {
    video: exStudentPT,
    name: "namePortuguese",
    description: "descriptionPortuguese",
  },
  es: {
    video: exStudentES,
    name: "nameSpanish",
    description: "descriptionSpanish",
  },
  fr: {
    video: exStudentFR,
    name: "nameFrench",
    description: "descriptionFrench",
  },
};

const infoPlans = {
  logo: {
    Essential: essentialCharacterPlans,
    Effective: effectiveCharacterPlans,
    Evolution: evolutionCharacterPlans,
  },
  subtitle: {
    Evolution: "PREMIUM_PLAN_FOR_MAXIMUM_PROGRESS",
    Essential: "IDEAL_FOR_RESUMING_YOUR_STUDIES_WITH_EASE",
    Effective: "MORE_FREQUENCY_TO_ACCELERATE_YOUR_PROGRESS",
  },
};

const HomeStudentPlanCancelled = () => {
  const videoRef = useRef(null);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const videoWrapperRef = useRef(null);
  const language = localStorage.getItem("i18nextLng");
  const { user } = useSelector((state) => state.userReducer);

  const langKey = (language || "en").split("-")[0].toLowerCase();
  const currentVideoSrc = languageMap[langKey].video || languageMap.en.video;

  const [cpn, setCpn] = useState("");
  const [duration, setDuration] = useState(0);
  const [buyPlans, setBuyPlans] = useState([]);
  const [isMuted, setIsMuted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);

  const progress = duration ? (currentTime / duration) * 100 : 0;

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      video.play();
      setIsPlaying(true);
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  const toggleMute = (event) => {
    event.stopPropagation();
    const video = videoRef.current;
    if (!video) return;

    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const toggleFullscreen = (event) => {
    event.stopPropagation();
    const wrapper = videoWrapperRef.current;
    if (!wrapper) return;

    if (document.fullscreenElement) document.exitFullscreen();
    else wrapper.requestFullscreen?.();
  };

  const loadPlans = useCallback(async () => {
    setLoading(true);

    const userPlans = await dispatch(getPlan(user.id));
    const { country_code } = await dispatch(getUserInformation());
    const responseUserInfo = await dispatch(getAdminUserInfo(`studentManagement/getbyuser/${user?.id}`));

    const countryURL = country_code === "BR" ? "BR" : "US";
    const isAffiliate = await dispatch(getAffiliateInfo(user?.id));

    const studentPlans = Array.isArray(userPlans) ? userPlans : [];
    const affiliateId = studentPlans[0]?.studentPlanPayments?.[0]?.affiliateId;

    const cpnCode = decryptIfEncrypted(responseUserInfo.result.items[0]?.cpnCode ?? "");
    setCpn(cpnCode);

    const exclusiveFor = isAffiliate
      ? "affiliate"
      : studentPlans || studentPlans[0]?.studentPlanPayments
      ? "partner"
      : affiliateId !== null
      ? "affiliate"
      : "website_stripe";

    const response = await dispatch(subscriptionsStripeGetPlans("", "", countryURL, exclusiveFor, true, null, cpnCode));

    const newPlans = response.map((plan) => {
      return { ...plan, totalClasses: plan.unlimited ? 9999 : plan.totalClasses, currency: countryURL };
    });

    setBuyPlans(newPlans);
    setLoading(false);
  }, [dispatch, user?.id]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const renderCardPlans = (plan, cpn, index) => {
    const parts = plan?.final_value.toString().split(".");
    const cents = parts.length > 1 ? parts[1] : "00";
    const currency = plan?.currency === "BR" ? "R$" : "USD";
    const cpnURL = cpn !== "" ? `&cpn=${cpn}` : "";

    return (
      <div
        key={index}
        className={`
          plan-card
          ${plan.evolution ? "plan-evolution" : ""}
          ${plan.ribbon ? "plan-card-ribboned" : ""}`}
      >
        {plan.bestSeller && (
          <div className="ribbon ribbon-evolution">
            <div className="ribbon-content">{t("BEST_SELLER")}</div>
          </div>
        )}

        <div className="plan-char-wrap">
          <img
            alt="logo Lingo"
            src={infoPlans.logo[plan.nameEnglish] ?? logoPNG}
            style={{ width: 62, height: 62, objectFit: "contain", display: "block" }}
          />
        </div>

        <div className="plan-body">
          <div className="plan-name-row">
            <span className="plan-name">{plan[languageMap[language].name]}</span>
          </div>

          <p className="plan-desc">{t(infoPlans.subtitle[plan.nameEnglish])}</p>

          <div className="plan-languages">
            <span className="plan-languages-label">{t("LANGUAGES")}:</span>
            <div className="plan-languages-flags">
              {plan?.languageUS && <FlagIcon code="US" size={20} />}
              {plan?.languageBR && <FlagIcon code="BR" size={20} />}
              {plan?.languageES && <FlagIcon code="ES" size={20} />}
              {plan?.languageFR && <FlagIcon code="FR" size={20} />}
              {plan?.multiLingo && (
                <>
                  <FlagIcon code="US" size={20} />
                  <FlagIcon code="BR" size={20} />
                  <FlagIcon code="ES" size={20} />
                  <FlagIcon code="FR" size={20} />
                </>
              )}
            </div>
          </div>
          <div className="plan-lessons">
            <svg
              width="13"
              height="13"
              fill="none"
              strokeWidth="2.5"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeLinecap="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            {plan[languageMap[language].description]}
          </div>
        </div>

        <div className="plan-right">
          <div>
            {plan?.discount_percent !== 0 && (
              <div className="plan-price-label-discount">
                <span className="plan-price-label">{currency}</span>
                <del>{plan?.baseValue.toFixed(2).replace(".", ",")}</del>
              </div>
            )}

            <div className="plan-price-label">{currency}</div>

            <div className="plan-price">
              <span className="plan-price-amount">{parts[0]}</span>
              <span className="plan-price-cents"> ,{cents}</span>
              <div className="plan-price-period">/ {t("MONTH")}</div>
            </div>
          </div>

          <a
            target="_top"
            rel="noreferrer"
            className="plan-btn plan-btn-blue"
            href={`/checkout?${plan?.link_made}&partner=null&changeType=checkout${cpnURL}&currency=${
              currency === "R$" ? "BRL" : "USD"
            }&internalCheckout=true`}
          >
            {t("SELECT")}
          </a>
        </div>
      </div>
    );
  };

  return (
    <div className="contentHomeStudentPlanCancelled">
      <div className="exaluno-grid">
        <div className="video-card">
          <div
            tabIndex="0"
            role="button"
            ref={videoWrapperRef}
            onClick={togglePlay}
            className="video-placeholder"
            aria-label="Assistir mensagem de boas-vindas"
            onKeyPress={(event) => {
              if (event.key === "Enter") togglePlay();
            }}
          >
            <video
              playsInline
              ref={videoRef}
              src={currentVideoSrc}
              className="video-element"
              onEnded={() => setIsPlaying(false)}
              onTimeUpdate={(event) => setCurrentTime(event.target.currentTime)}
              onLoadedMetadata={(event) => setDuration(event.target.duration)}
            />

            {!isPlaying && (
              <div className="video-ph-content">
                <div className="video-play-circle">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
                    <polygon points="5 3 19 12 5 21 5 3" />
                  </svg>
                </div>
              </div>
            )}

            <div className="video-ph-controls">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="white"
                style={{ flexShrink: 0 }}
                onClick={(event) => {
                  event.stopPropagation();
                  togglePlay();
                }}
              >
                {isPlaying ? (
                  <>
                    <rect x="6" y="4" width="4" height="16" />
                    <rect x="14" y="4" width="4" height="16" />
                  </>
                ) : (
                  <polygon points="5 3 19 12 5 21 5 3" />
                )}
              </svg>
              <span className="video-ph-time">
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>
              <div className="video-ph-bar">
                <div className="video-ph-bar-fill" style={{ width: `${progress}%` }} />
              </div>
              <svg
                width="15"
                height="15"
                fill="none"
                strokeWidth="2"
                viewBox="0 0 24 24"
                onClick={toggleMute}
                strokeLinecap="round"
                style={{ flexShrink: 0 }}
                stroke="rgba(255,255,255,0.8)"
              >
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                {!isMuted && <path d="M15.54 8.46a5 5 0 010 7.07" />}
                {isMuted && <line x1="17" y1="9" x2="22" y2="14" />}
                {isMuted && <line x1="22" y1="9" x2="17" y2="14" />}
              </svg>
              <svg
                width="15"
                height="15"
                fill="none"
                strokeWidth="2"
                viewBox="0 0 24 24"
                strokeLinecap="round"
                style={{ flexShrink: 0 }}
                onClick={toggleFullscreen}
                stroke="rgba(255,255,255,0.8)"
              >
                <polyline points="15 3 21 3 21 9" />
                <polyline points="9 21 3 21 3 15" />
                <line x1="21" y1="3" x2="14" y2="10" />
                <line x1="3" y1="21" x2="10" y2="14" />
              </svg>
            </div>
          </div>
        </div>

        <div className="plans-col">
          {loading ? (
            <div className="loading">
              <ClipLoader color="#004fff" />
            </div>
          ) : (
            buyPlans
              ?.filter((item) => item.active && item.activeWeb)
              ?.map((item, index) => renderCardPlans(item, cpn, index))
          )}
        </div>
      </div>
    </div>
  );
};

export default HomeStudentPlanCancelled;
